import { ProcessParameter } from "../../../types/mes";
import { processParamTimeToInput } from "../../../utils/processParameterSerialize";
import { t } from "../../../i18n/locale";

/**
 * Procesni parametri po proizvodnoj fazi — JEDAN izvor istine.
 *
 * "Procesni podaci" i "Radni nalozi (tablet)" pišu u istu tablicu
 * (zboogie_process_parameters, jedan red po zamjesu), ali su dosad imali dvije
 * neovisne liste polja: unos na tabletu je pokrivao 7–8 polja, a ekran procesnih
 * podataka preko 20. Operater je zato na nalogu mogao upisati samo dio onoga što
 * se od faze traži, a tehnolog je ostatak dopisivao naknadno.
 *
 * Oba ekrana sada renderiraju polja iz ovog kataloga, pa se ne mogu razići:
 * tablet uzme grupe faza koje njegov tab pokriva, a procesni podaci sve grupe.
 *
 * Kodovi grupa su ProductionPhaseType.Code (mixing, bulk_fermentation…), uz dvije
 * grupe koje nisu proizvodna faza nego mjesto mjerenja: `quality` i `notes`.
 */

export type ProcessFieldType = "number" | "text" | "time" | "textarea";

export interface ProcessField {
  /** Naziv kolone u ProcessParameter — ujedno ključ u formi. */
  key: string;
  label: string;
  type: ProcessFieldType;
  step?: string;
  /**
   * Polje se izračunava iz drugih (npr. Δ pH). Prikazuje se samo za čitanje, a
   * u bazu se sprema kroz `derivedProcessValues` — kolone za to postoje i
   * izvještaji ih čitaju, pa ih ne ostavljamo prazne.
   */
  derived?: boolean;
  /** Kratko objašnjenje ispod polja (jedinice, osnova izračuna…). */
  hint?: string;
}

export interface ProcessPhaseGroup {
  code: string;
  label: string;
  fields: ProcessField[];
}

export const PROCESS_PHASE_GROUPS: ProcessPhaseGroup[] = [
  {
    code: "mixing",
    label: "Miješanje",
    fields: [
      { key: "MixingStart", label: "Početak miješanja", type: "time" },
      { key: "MixingEnd", label: "Kraj miješanja", type: "time" },
      { key: "DoughMassKg", label: "Masa zamjesa (kg)", type: "number", step: "0.1" },
      { key: "CapacityKg", label: "Kapacitet miksera (kg)", type: "number", step: "0.1" },
      { key: "FillPct", label: "Napunjenost (%)", type: "number", derived: true, hint: "masa / kapacitet" },
      { key: "DoughTempC", label: "Temperatura tijesta (°C)", type: "number", step: "0.1" },
      { key: "StarterPh", label: "pH startera", type: "number", step: "0.01" },
      { key: "BulkFermentationPh1", label: "pH zamjesa", type: "number", step: "0.01" },
      { key: "BulkFermentationPh2", label: "pH (Rheon)", type: "number", step: "0.01" },
      { key: "BulkFermentationDeltaPh", label: "Δ pH", type: "number", derived: true, hint: "pH (Rheon) − pH zamjesa" },
      { key: "EnergyPeakWh", label: "Energija — vrh (Wh)", type: "number", step: "0.1" },
      { key: "EnergyEndWh", label: "Energija — kraj (Wh)", type: "number", step: "0.1" },
    ],
  },
  {
    code: "bulk_fermentation",
    label: "Fermentacija",
    fields: [
      { key: "BulkFermentationStart", label: "Početak fermentacije", type: "time" },
      { key: "BulkFermentationEnd", label: "Kraj fermentacije", type: "time" },
      { key: "BulkFermentationHeight1", label: "Vis. mjerica (Mikser)", type: "number", step: "1" },
      { key: "BulkFermentationHeight2", label: "Vis. mjerica (Rheon)", type: "number", step: "1" },
      {
        key: "BulkFermentationDeltaHeightPct",
        label: "Δ visina (%)",
        type: "number",
        derived: true,
        hint: "prirast od prve mjerice",
      },
      { key: "FermentationType", label: "Tip fermentacije", type: "text" },
    ],
  },
  {
    code: "final_proof",
    label: "Završno dizanje",
    fields: [
      { key: "FinalProofStart", label: "Početak završnog dizanja", type: "time" },
      { key: "FinalProofEnd", label: "Kraj završnog dizanja", type: "time" },
      { key: "FinalProofHeight1", label: "Visina 1", type: "number", step: "1" },
      { key: "FinalProofHeight2", label: "Visina 2", type: "number", step: "1" },
      {
        key: "FinalProofDeltaHeightPct",
        label: "Δ visina (%)",
        type: "number",
        derived: true,
        hint: "prirast od prve mjerice",
      },
    ],
  },
  {
    code: "baking",
    label: "Pečenje",
    fields: [
      { key: "BakingStart", label: "Početak pečenja", type: "time" },
      { key: "BakingEnd", label: "Kraj pečenja", type: "time" },
      { key: "BakingTempC", label: "Temperatura pečenja (°C)", type: "number", step: "1" },
      { key: "PrebakePh", label: "pH prije pečenja", type: "number", step: "0.01" },
    ],
  },
  {
    code: "cooling",
    label: "Hlađenje",
    fields: [{ key: "CoolingStart", label: "Početak hlađenja", type: "time" }],
  },
  {
    code: "packaging",
    label: "Pakiranje",
    fields: [
      { key: "PackagingStart", label: "Početak pakiranja", type: "time" },
      { key: "PackagingEnd", label: "Kraj pakiranja", type: "time" },
      { key: "ProducedPieces", label: "Proizvedeno (kom)", type: "number", step: "1" },
      { key: "PackedPieces", label: "Zapakirano (kom)", type: "number", step: "1" },
      { key: "ScrapShape", label: "Škart po obliku (kom)", type: "number", step: "1" },
      { key: "ScrapColor", label: "Škart po boji (kom)", type: "number", step: "1" },
      { key: "Scrap", label: "Škart ukupno (kom)", type: "number", derived: true, hint: "oblik + boja" },
      { key: "ScrapPct", label: "Škart (%)", type: "number", derived: true, hint: "od proizvedenih komada" },
    ],
  },
  {
    code: "quality",
    label: "Kvaliteta",
    fields: [{ key: "SpecificVolumeLkg", label: "Spec. volumen (L/kg)", type: "number", step: "0.001" }],
  },
  {
    code: "notes",
    label: "Napomena",
    fields: [{ key: "Notes", label: "Napomena", type: "textarea" }],
  },
];

/** Grupe koje pokrivaju zadane kodove faza, u kanonskom redoslijedu. */
export function localizeProcessGroup(group: ProcessPhaseGroup): ProcessPhaseGroup {
  return {
    ...group,
    label: t(group.label),
    fields: group.fields.map((field) => ({
      ...field,
      label: t(field.label),
      hint: field.hint ? t(field.hint) : field.hint,
    })),
  };
}

export function processGroupsForPhases(codes: string[]): ProcessPhaseGroup[] {
  return PROCESS_PHASE_GROUPS.filter((g) => codes.includes(g.code)).map(localizeProcessGroup);
}

/** Sva polja zadanih grupa (uključujući izračunata), u redoslijedu grupa. */
export function processFieldsForGroups(groups: ProcessPhaseGroup[]): ProcessField[] {
  return groups.flatMap((g) => g.fields);
}

export function processFieldsForPhases(codes: string[]): ProcessField[] {
  return processFieldsForGroups(processGroupsForPhases(codes));
}

const num = (form: Record<string, string>, key: string): number | null => {
  const raw = form[key];
  if (raw == null || String(raw).trim() === "") return null;
  const n = Number(String(raw).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

const round = (n: number, decimals: number) => {
  const f = 10 ** decimals;
  return Math.round(n * f) / f;
};

/**
 * Izračunata polja iz unesenih vrijednosti. Vraća samo ona čiji su ulazi
 * popunjeni — polje bez ulaza ostaje null da ne prepiše postojeću vrijednost
 * nulom.
 */
export function derivedProcessValues(form: Record<string, string>): Record<string, number | null> {
  const out: Record<string, number | null> = {};

  const mass = num(form, "DoughMassKg");
  const capacity = num(form, "CapacityKg");
  if (mass != null && capacity != null && capacity > 0) out.FillPct = round((mass / capacity) * 100, 2);

  const ph1 = num(form, "BulkFermentationPh1");
  const ph2 = num(form, "BulkFermentationPh2");
  if (ph1 != null && ph2 != null) out.BulkFermentationDeltaPh = round(ph2 - ph1, 3);

  const h1 = num(form, "BulkFermentationHeight1");
  const h2 = num(form, "BulkFermentationHeight2");
  if (h1 != null && h2 != null && h1 > 0) out.BulkFermentationDeltaHeightPct = round(((h2 - h1) / h1) * 100, 2);

  const fp1 = num(form, "FinalProofHeight1");
  const fp2 = num(form, "FinalProofHeight2");
  if (fp1 != null && fp2 != null && fp1 > 0) out.FinalProofDeltaHeightPct = round(((fp2 - fp1) / fp1) * 100, 2);

  // Škart se unosi po uzroku (oblik / boja); ukupno je izvedeno da se ne može
  // razići od svojih dijelova, a % se veže na proizvedene komade (fallback:
  // zapakirano + škart, kad broj proizvedenih nije upisan).
  const shape = num(form, "ScrapShape");
  const color = num(form, "ScrapColor");
  if (shape != null || color != null) {
    const total = (shape ?? 0) + (color ?? 0);
    out.Scrap = total;

    const produced = num(form, "ProducedPieces");
    const packed = num(form, "PackedPieces");
    const basis = produced != null && produced > 0 ? produced : (packed ?? 0) + total;
    if (basis > 0) {
      out.ScrapPct = round((total / basis) * 100, 2);
      out.ScrapShapePct = round(((shape ?? 0) / basis) * 100, 2);
      out.ScrapColorPct = round(((color ?? 0) / basis) * 100, 2);
    }
  }

  return out;
}

/**
 * Vrijednosti forme iz spremljenog reda. `fields` određuje koja se polja čitaju,
 * pa isti pomoćnik služi i tabletu (jedna faza) i ekranu procesnih podataka
 * (sve faze).
 */
export function buildProcessForm(
  param: ProcessParameter | Record<string, unknown> | undefined,
  fields: ProcessField[]
): Record<string, string> {
  const raw = (param ?? {}) as Record<string, unknown>;
  const form: Record<string, string> = {};
  for (const field of fields) {
    const value = raw[field.key];
    if (field.type === "time") {
      form[field.key] = processParamTimeToInput(value);
    } else {
      form[field.key] = value == null ? "" : String(value);
    }
  }
  return form;
}

/**
 * Payload za POST/PUT process-parameters iz vrijednosti forme. Piše SAMO polja
 * koja su u `fields` (plus njihova izračunata), pa spremanje jedne faze na
 * tabletu ne pobriše ono što je druga faza upisala.
 */
export function processPayloadFromForm(
  form: Record<string, string>,
  fields: ProcessField[]
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  for (const field of fields) {
    if (field.derived) continue;
    const raw = form[field.key] == null ? "" : String(form[field.key]).trim();
    if (raw === "") {
      payload[field.key] = null;
    } else if (field.type === "number") {
      const n = Number(raw.replace(",", "."));
      payload[field.key] = Number.isFinite(n) ? n : null;
    } else {
      payload[field.key] = raw;
    }
  }

  const derived = derivedProcessValues(form);
  const derivedKeys = fields.filter((f) => f.derived).map((f) => f.key);
  // Kolone kojima nema vlastitog polja, a slijede iz upisanih (npr. ScrapShapePct
  // uz Scrap) — spremaju se kad je njihov izvor u ovom setu polja.
  const IMPLIED: Record<string, string[]> = {
    Scrap: ["ScrapShapePct", "ScrapColorPct"],
  };

  for (const key of derivedKeys) {
    // `?? null` je namjerno: kad se ulazi isprazne, izračunata kolona se čisti
    // umjesto da zadrži staru vrijednost izvedenu iz podataka kojih više nema.
    payload[key] = derived[key] ?? null;
    for (const implied of IMPLIED[key] ?? []) {
      payload[implied] = derived[implied] ?? null;
    }
  }

  return payload;
}

/** Vrijednost izračunatog polja za prikaz (prazno kad ulazi nisu popunjeni). */
export function derivedDisplayValue(form: Record<string, string>, key: string): string {
  const value = derivedProcessValues(form)[key];
  return value == null ? "" : String(value);
}
